import { Box, MenuItem } from "@mui/material";
import { TextField } from "@platform/design-system";
import {
  CommissionDetail,
  commissionTypeLabel,
} from "@platform/formatting-utils";
import { ERROR_MESSAGES } from "@platform/rfp-core";
import { JSX } from "react";

import { CommissionDetailFields } from "./CommissionDetailFields";

export interface CommissionFieldProps {
  label: string;
  options: readonly string[];
  value?: CommissionDetail;
  error?: string;
  required?: boolean;
  testId: string;
  /** Stores the detail without validating, for edits still in progress. */
  onChange: (detail: CommissionDetail) => void;
  /** Stores and validates, for a value the broker has finished choosing. */
  onCommit?: (detail: CommissionDetail) => void;
  /** Fires as the field is entered, so the fields above it can validate. */
  onEnter?: () => void;
}

/** Types that reveal their own inputs below the dropdown. */
const TYPES_WITH_DETAIL_FIELDS: readonly string[] = [
  "percentage",
  "pepm",
  "heaped",
  "graded",
];

/**
 * Errors the amount input owns. Anything else describes the selection, so it
 * belongs against the dropdown.
 */
const AMOUNT_MESSAGES: readonly string[] = [
  ERROR_MESSAGES.COMMISSION_AMOUNT_REQUIRED,
  ERROR_MESSAGES.COMMISSION_PERCENTAGE_REQUIRED,
  ERROR_MESSAGES.COMMISSION_PEPM_MAX,
  ERROR_MESSAGES.COMMISSION_HEAPED_PAIR_REQUIRED,
];

/**
 * Clears an optional field. Not a stored value — it maps to no type at all.
 * Labelled the way the BTX grid renders a commission it has no value for.
 */
const NO_SELECTION = "";
const NO_SELECTION_LABEL = "-";

/**
 * One commission side: the type dropdown plus whatever detail that type needs.
 *
 * Percentage, PEPM, heaped and graded reveal their inputs below the dropdown;
 * the rest are complete on selection. Changing the type always emits a detail
 * carrying only the new type, so a previous amount is never submitted under it.
 */
export const CommissionField = ({
  label,
  options,
  value,
  error,
  required,
  testId,
  onChange,
  onCommit,
  onEnter,
}: CommissionFieldProps): JSX.Element => {
  const type = value?.type ?? "";

  const commit = onCommit ?? onChange;

  const amountError =
    error && AMOUNT_MESSAGES.includes(error) ? error : undefined;
  const selectError = amountError ? undefined : error;

  // A selection is complete the moment it is made, so it validates straight
  // away. A type that reveals further inputs is only stored: demanding a number
  // the broker has not reached yet is noise.
  const handleTypeChange = (nextType: string): void => {
    const detail = nextType === NO_SELECTION ? {} : { type: nextType };

    if (TYPES_WITH_DETAIL_FIELDS.includes(nextType)) {
      onChange(detail);
    } else {
      commit(detail);
    }
  };

  return (
    <Box data-testid={testId}>
      <TextField
        select
        fullWidth
        required={required}
        label={label}
        value={type}
        error={!!selectError}
        helperText={selectError}
        onFocus={onEnter}
        onChange={(event): void => handleTypeChange(event.target.value)}
        data-testid={`${testId}-select`}
      >
        {!required && (
          <MenuItem value={NO_SELECTION}>{NO_SELECTION_LABEL}</MenuItem>
        )}
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {commissionTypeLabel(option)}
          </MenuItem>
        ))}
      </TextField>

      <CommissionDetailFields
        // Remounted per type: the heaped drafts and the graded mode belong to
        // the type that was picked, and must not survive a switch away and back.
        key={type}
        type={type}
        value={value}
        error={amountError}
        testId={testId}
        required={required}
        onChange={onChange}
        onCommit={commit}
        onEnter={onEnter}
      />
    </Box>
  );
};
